import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { BillingStatusBadge } from "../../components/billing/BillingBadges";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import { isApiError, listInvoices } from "../../lib/billing-api";
import type { Invoice } from "../../lib/billing-types";
import { formatDate, formatMoney } from "../../lib/format";
import { useConfig } from "../../state/config-context";

function PaymentsInner(): ReactNode {
  const { t, lang } = useConfig();
  const [items, setItems] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const locale = lang === "es" ? "es-ES" : "en-US";

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const page = await listInvoices({ status: "paid", limit: 50 });
      setItems(page.items);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="page" aria-labelledby="billing-payments-title">
      <header className="page__header">
        <h1 id="billing-payments-title">{t("billing.payments.title")}</h1>
        <p className="muted">{t("billing.payments.subtitle")}</p>
      </header>

      <p className="muted">{t("billing.payments.hint")}</p>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && items.length === 0 ? (
        <p className="empty">{t("billing.payments.empty")}</p>
      ) : null}

      {!loading && items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("billing.payments.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("billing.invoice.number")}</th>
                <th scope="col">{t("billing.invoice.account")}</th>
                <th scope="col">{t("billing.invoice.status")}</th>
                <th scope="col">{t("billing.invoice.paidAt")}</th>
                <th scope="col">{t("billing.invoice.total")}</th>
                <th scope="col" className="table__actions-col">
                  <span className="sr-only">{t("billing.common.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <Link to={`/billing/invoices/${invoice.id}`}>
                      {invoice.number === null
                        ? t("billing.quote.draftNumber")
                        : `#${String(invoice.number)}`}
                    </Link>
                  </td>
                  <td>{invoice.account_id}</td>
                  <td>
                    <BillingStatusBadge status={invoice.status} />
                  </td>
                  <td>{formatDate(invoice.paid_at, locale, true)}</td>
                  <td>{formatMoney(invoice.total_minor, invoice.currency, locale)}</td>
                  <td className="table__actions-col">
                    <Link className="btn btn--ghost btn--sm" to={`/billing/invoices/${invoice.id}`}>
                      {t("billing.common.view")}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

export function PaymentsPage(): ReactNode {
  return (
    <RequirePermission permission="billing.invoice.read">
      <PaymentsInner />
    </RequirePermission>
  );
}
